import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Search, 
  Edit, 
  Trash2, 
  Key,
  User,
  CheckCircle,
  Shield
} from "lucide-react";

export default function AdminRegistrants() {
  const { token, user: currentUser } = useAuth();
  const [registrants, setRegistrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [editForm, setEditForm] = useState({
    full_name: "",
    telephone: "",
    email: "",
    status: ""
  });

  useEffect(() => {
    fetchRegistrants();
  }, []);

  const fetchRegistrants = async () => {
    try {
      const response = await axios.get(`${API}/admin/registrants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrants(response.data);
    } catch (error) {
      toast.error("Failed to load registrants");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      full_name: user.full_name || "",
      telephone: user.telephone || "",
      email: user.email || "",
      status: user.status || "Pending Approval"
    });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(
        `${API}/admin/registrants/${editingUser.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Registrant updated successfully");
      setEditingUser(null);
      fetchRegistrants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update registrant");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${API}/admin/registrants/${deletingUser.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Registrant deleted successfully");
      setDeletingUser(null);
      fetchRegistrants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete registrant");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/reset-password`,
        { user_id: resetPasswordUser.id, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password reset successfully. User must change password on next login.");
      setResetPasswordUser(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleApproveRegistration = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/approve-registration/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Registration approved successfully");
      fetchRegistrants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve registration");
    }
  };

  const handleApproveManager = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/approve-manager/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Manager approved successfully");
      fetchRegistrants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve manager");
    }
  };

  const getStatusBadge = (user) => {
    if (!user.registration_approved) {
      return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Pending Registration</Badge>;
    }
    if (user.user_type === "manager" && user.role === "pending_manager") {
      return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">Pending Manager Approval</Badge>;
    }
    switch (user.status) {
      case "Approved":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Approved</Badge>;
      case "Pending Approval":
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pending</Badge>;
      case "Denied":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Denied</Badge>;
      default:
        return <Badge variant="secondary">{user.status || "Unknown"}</Badge>;
    }
  };

  const getRoleBadge = (user) => {
    if (user.role === "manager") {
      return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">Manager</Badge>;
    }
    if (user.user_type === "tenant") {
      return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Tenant</Badge>;
    }
    if (user.user_type === "applicant") {
      return <Badge className="bg-stone-100 text-stone-800 hover:bg-stone-100">Applicant</Badge>;
    }
    return null;
  };

  const filteredRegistrants = registrants.filter(user => 
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.telephone?.includes(searchTerm)
  );

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-registrants-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-primary">Registrants</h1>
              <p className="text-text-muted mt-1">Manage all registered users</p>
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                data-testid="search-registrants-input"
                placeholder="Search by name, username, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 bg-white border-stone-200"
              />
            </div>
          </div>

          {/* Registrants Table */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg">All Registrants ({filteredRegistrants.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-primary">Loading...</div>
                </div>
              ) : filteredRegistrants.length === 0 ? (
                <div className="text-center py-8">
                  <User className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-text-muted">No registrants found</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="table-custom">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Username</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRegistrants.map((user) => (
                        <tr key={user.id} data-testid={`registrant-row-${user.id}`}>
                          <td className="font-medium">
                            {user.full_name}
                            <div className="mt-1">{getRoleBadge(user)}</div>
                          </td>
                          <td>{user.username}</td>
                          <td>{user.telephone}</td>
                          <td>{user.email || "-"}</td>
                          <td>{getStatusBadge(user)}</td>
                          <td>
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Approve Registration Button */}
                              {!user.registration_approved && (
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => handleApproveRegistration(user.id)}
                                  data-testid={`approve-registration-btn-${user.id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                              )}
                              {/* Approve Manager Button */}
                              {user.user_type === "manager" && user.role === "pending_manager" && currentUser?.role === "admin" && (
                                <Button
                                  size="sm"
                                  className="bg-purple-600 hover:bg-purple-700 text-white"
                                  onClick={() => handleApproveManager(user.id)}
                                  data-testid={`approve-manager-btn-${user.id}`}
                                >
                                  <Shield className="w-4 h-4 mr-1" />
                                  Make Manager
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(user)}
                                data-testid={`edit-btn-${user.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setResetPasswordUser(user)}
                                data-testid={`reset-password-btn-${user.id}`}
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeletingUser(user)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-btn-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Registrant</DialogTitle>
            <DialogDescription>Update the registrant's information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_full_name">Full Name</Label>
              <Input
                id="edit_full_name"
                data-testid="edit-full-name-input"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_telephone">Telephone</Label>
              <Input
                id="edit_telephone"
                data-testid="edit-telephone-input"
                value={editForm.telephone}
                onChange={(e) => setEditForm({ ...editForm, telephone: e.target.value })}
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                data-testid="edit-email-input"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                <SelectTrigger className="h-12" data-testid="edit-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending Approval">Pending Approval</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Denied">Denied</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-primary hover:bg-primary-800" data-testid="save-edit-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={() => { setResetPasswordUser(null); setNewPassword(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for {resetPasswordUser?.full_name}. The user will be required to change their password on next login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                data-testid="new-password-reset-input"
                type="password"
                placeholder="Enter new password (min. 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="h-12"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetPasswordUser(null); setNewPassword(""); }}>Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-primary hover:bg-primary-800" data-testid="confirm-reset-password-btn">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingUser} onOpenChange={() => setDeletingUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Registrant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deletingUser?.full_name}? This action cannot be undone and will also delete all their applications.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
